<template>
  <v-container id="fileList" fluid class="text-center black--text subtitle-1">
    <v-row>
      <v-col>
        <v-progress-linear :active="innerLoading" color="loader" indeterminate />
      </v-col>
    </v-row>
    <ValidationObserver ref="filesList">
      <v-data-iterator
          :items="documentList"
          hide-default-footer
          disable-pagination
          disable-filtering
          item-key="documentType"
          :locale="systemLocale">
        <template v-slot="props">
          <v-row justify="center">
            <v-col
                v-for="item in props.items"
                :key="item.documentType"
                cols="12"
                sm="10"
                md="5"
                xl="4">
              <v-card>
                <v-card-title>
                  <v-row
                      class="v-label textcolor--text subtitle-2 font-weight-bold"
                      justify="start">
                    <v-col
                        v-if="item.required"
                        cols="1"
                        class="red--text"
                        align-self="center">
                      *
                    </v-col>
                    <v-col
                        :cols="item.required ? 7 : 8"
                        class="text-left"
                        align-self="center">
                      <link-with-tip
                          v-if="item.templatePath"
                          :href="item.templatePath"
                          :tip="$t('Скачать шаблон документа')"
                          :text="$t(item.documentDescription)" />
                      <span v-else class="word_break">
                        {{ $t(item.documentDescription) }}
                      </span>
                    </v-col>
                    <v-col
                        cols="4">
                      <v-row
                          class="text-right pl-1 pr-2"
                          justify="end"
                          align-content="center">

                        <v-col v-if="!readonly" cols="4">
                          <typed-file-upload
                              v-if="!readonly"
                              :document-type="item.documentType"
                              :document-group="item.documentGroup"
                              :upload-method="uploadBusinessFile" />
                        </v-col>
                        <v-col v-if="!readonly" cols="4">
                          <btn-icon-tip
                              v-if="item.fileId && !readonly"
                              :id="`deleteFile-${item.fileId}`"
                              icon="delete"
                              :tip="$t('Удалить файл')"
                              @click="removeFile(item.fileId)" />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-divider />
                <v-row class="v-label textcolor--text font-weight-bold py-3 pr-1">
                  <v-col cols="12" class="text-left px-6">
                    <ValidationObserver :ref="item.documentType">
                      <validatable-text-input
                          :key="item.documentType"
                          v-model="item.fileName"
                          :rules="{ required: !!item.required }"
                          :label="$t(item.documentDescription)"
                          prepend-icon="attach_file"
                          :vid="item.documentType"
                          readonly />
                    </ValidationObserver>
                  </v-col>
                </v-row>
                <v-divider v-if="item.fileId" />
                <v-card-text>
                  <v-row>
                    <v-col v-if="item.fileId" cols="12" class="text-center">
                      <btn-icon-tip
                          :id="`getFile-${item.fileId}`"
                          icon="get_app"
                          :tip=" $t('Скачать документ')"
                          @click="getBusinessFile(item.fileId)" />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </v-data-iterator>
    </ValidationObserver>
  </v-container>
</template>

<style scoped>

.word_break {
  word-break: break-word;
}

</style>

<script>
  import Vue from 'vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import TypedFileUpload from '@/components/reused/ui/TypedFileUpload.vue'
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import LinkWithTip from '@/components/reused/ui/LinkWithTip.vue'
  import getFileMixin from '@/utils/mixins/scoped/getFileMixin.js'
  import { mapActions } from 'vuex'
  import { BUSINESS_FILES } from '@/constants/store.js'

  export default {
    components: {
      TypedFileUpload,
      BtnIconTip,
      LinkWithTip,
      ValidatableTextInput
    },
    mixins: [
      getFileMixin('isLoading')
    ],
    props: {
      documentTypes: {
        type: Array,
        default: () => {
          return []
        }
      },
      requestFiles: {
        type: Array,
        default: () => {
          return []
        }
      },
      readonly: {
        type: Boolean,
        default: false
      },
      validationRequired: {
        type: Boolean,
        default: true
      },
      loading: {
        type: Boolean,
        default: false
      },
      lotRequestInfo: {
        type: Object,
        default: () => { return {} }
      }
    },
    data () {
      return {
        isLoading: false
      }
    },
    computed: {
      innerLoading () {
        return this.isLoading || this.loading
      },
      documentList () {
        const vm = this

        const requiredDocumentTypes = vm.documentTypes || []
        const requestFiles = vm.requestFiles || []
        const list = requiredDocumentTypes.map(item => {
          const templateName = vm.getDocumentTemplateName(item.documentType, `${vm.lotRequestInfo.lotType}_${vm.systemLocale}`)
          const templatePath = templateName
            ? vm.getSystemFileUrl(templateName)
            : null
          const savedFile = requestFiles.find(f => f.documentType === item.documentType)

          if (savedFile) {
            return {
              ...item,
              ...savedFile,
              templatePath
            }
          }
          return {
            ...item,
            templatePath
          }
        })
        return list
      }
    },
    methods: {
      ...mapActions(BUSINESS_FILES.NAMESPACE, {
        removeFileFromAttachmentAction: BUSINESS_FILES.ACTIONS.REMOVE_FILE_FROM_ATTACHMENT,
        uploadBusinessFileAction: BUSINESS_FILES.ACTIONS.UPLOAD_BUSINESS_FILE
      }),
      removeFile: async function (fileId) {
        try {
          await this.removeFileFromAttachmentAction({
            ...this.lotRequestInfo,
            fileId
          })
          this.$emit('deleted')
        } catch (error) {
          this.throwIfNotServerError(error)
        }
      },
      uploadBusinessFile: async function (form, documentType, documentGroup) {
        // try catch не нужен (если ошибка, обработаем в месте вызова метода)
        const creatorId = this.lotRequestInfo.creatorId
        if (creatorId && this.isAdmin) {
          form.append('creatorId', creatorId)
        }
        const file = await this.uploadBusinessFileAction({
          form,
          documentType
        })
        await this.$refs[documentType][0].validate()
        this.$emit('uploaded', file, documentType, documentGroup)
      },
      validate: async function () {
        if (!this.validationRequired) {
          return
        }
        const result = await this.$refs.filesList.validate()
        if (!result) {
          throw new Vue.errorsTypes.ValidationError()
        }
      }
    }
  }
</script>
